import { FormBuilder } from '@/components/formBuilder';
import { ProjectOptions } from '@/components/shared/project-options';
import { reviewsApiActions } from '@/store/reviews';
import { SanitizedText } from '@/ui/SanitizedDom';
import {
  Box,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  Button,
  IconButton,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks/useShowToast';
import { useKFlags } from '@/hooks/use-k-flags';
import { ChevronDown, Plus } from '@kula-ai/icons-core';

const pendingReview = {
  label: 'Pending Review',
  color: '#B54708',
  bg: '#FFFAEB',
  id: 'pending_review',
  value: 'pending_review',
};

// eslint-disable-next-line react-refresh/only-export-components -- status config is co-located with its consumer component and re-exported via the barrel for existing callers
export const reviewsStatusConfig = {
  request_email_failed: {
    ...pendingReview,
    originalStatus: 'request_email_failed',
  },
  request_email_succeeded: {
    ...pendingReview,
    originalStatus: 'request_email_succeeded',
  },
  review_requested: { ...pendingReview, originalStatus: 'review_requested' },

  marked_interested: {
    label: 'Good Fit',
    color: '#027A48',
    bg: '#ECFDF3',
    id: 'interested',
    value: 'marked_interested',
  },
  marked_not_interested: {
    id: 'not_interested',
    label: 'Not a Good Fit',
    color: '#B42318',
    bg: '#FEF3F2',
    value: 'marked_not_interested',
  },
};

const statusConfig = [
  {
    id: 'interested',
    label: 'Good Fit',
    color: '#027A48',
    bg: '#ECFDF3',
    value: 'marked_interested',
  },
  {
    id: 'not_interested',
    label: 'Not a Good Fit',
    color: '#B42318',
    bg: '#FEF3F2',
    value: 'marked_not_interested',
  },
];

export const UpdateReviewStatus = ({
  status = '',
  reviewId = undefined,
  isUpdateAllowed = false,
  isCreateAllowed = false,
  contact = {},
  prospectId = undefined,
  onRefetch = () => {},
  isFromAts = false,
}) => {
  const formRef = useRef();
  const toast = useShowToast();
  const { ats_enabled } = useKFlags();

  const [currentStatus, setStatus] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const modalActions = useDisclosure();
  const dispatch = useDispatch();
  const { color, label = '-', bg, id } = reviewsStatusConfig[status] || {};
  const { name } = contact;

  const handleStatusChange = async (formValues) => {
    setIsLoading(true);
    const urlPaths = {
      reviewId,
      status: `status`,
    };

    const requestConfig = { status: currentStatus?.id, ...formValues };
    const toastConfig = {};

    const result = await dispatch(
      reviewsApiActions('updateReviewStatus', {
        urlPaths,
        requestConfig,
        isApi: true,
      })
    );
    if (result?.errors?.length) {
      toastConfig.title = result?.errors?.[0];
      toastConfig.status = 'error';
      setIsLoading(false);
    } else {
      toastConfig.title = 'Candidate Status updated';
      toastConfig.status = 'success';
      onRefetch();
      setIsLoading(false);
    }
    toast({
      ...toastConfig,
      position: 'bottom-left',
      isClosable: true,
    });
    onClose();
  };

  const statusList = () => {
    if (ats_enabled && isUpdateAllowed) {
      return (
        <Menu isLazy>
          <MenuButton textAlign="left">
            <Flex
              alignItems="center"
              cursor="pointer"
              bgColor={bg}
              padding={'2px 8px'}
              borderRadius={'6px'}
              size={'sm-medium'}
              textAlign="center"
              border="1px"
              borderColor="transparent"
              _groupHover={{ bg: 'white', borderColor: 'border-color' }}
              gap="2px"
            >
              <KText color={color}>{label}</KText>
              <Icon as={ChevronDown} _groupHover={{ display: 'block' }} />
            </Flex>
          </MenuButton>
          <MenuList width="175px">
            <Flex
              gap="5px"
              mt="10px"
              flexWrap="wrap"
              flexDirection="column"
              margin={isFromAts ? '2px' : '5px'}
            >
              {statusConfig
                ?.filter((status) => status?.id !== id)
                ?.map((status) => {
                  const { color, label, id: sId, bg } = status;
                  if (id !== sId) {
                    return (
                      <Flex
                        key={sId}
                        alignItems="center"
                        cursor="pointer"
                        p="5px 10px"
                        borderRadius={isFromAts ? '6px' : '12px'}
                        _hover={{ background: '#F5F8FF' }}
                        _focus={{ background: '#F5F8FF' }}
                        onClick={() => {
                          onOpen();
                          setStatus(status);
                        }}
                      >
                        {/* {!isFromAts  && (
                          <Box
                            width="6px"
                            height="6px"
                            bg={color}
                            borderRadius="full"
                            marginRight="8px"
                          />
                        )} */}
                        <KText
                          p="3px 10px"
                          borderRadius={'6px'}
                          color={color}
                          bgColor={bg}
                          size={'sm-medium'}
                        >
                          {label}
                        </KText>
                      </Flex>
                    );
                  }
                  return null;
                })}
            </Flex>
          </MenuList>
        </Menu>
      );
    }
    if (!ats_enabled && isUpdateAllowed)
      return (
        <Menu isLazy>
          <MenuButton textAlign="left">
            <Flex
              alignItems="center"
              cursor="pointer"
              bgColor={bg}
              padding={'2px 8px'}
              borderRadius={'6px'}
              size={'sm-medium'}
              borderColor="transparent"
              textAlign="center"
              _groupHover={{ bg: 'white', borderColor: 'border-color' }}
              gap="2px"
            >
              <KText color={color}>{label}</KText>
              <Icon as={ChevronDown} _groupHover={{ display: 'block' }} />
            </Flex>
          </MenuButton>
          <MenuList width="175px">
            <Flex
              gap="5px"
              mt="10px"
              flexWrap="wrap"
              flexDirection="column"
              margin={'2px'}
            >
              {statusConfig
                ?.filter((status) => status?.id !== id)
                ?.map((status) => {
                  const { color, label, id: sId, bg } = status;
                  if (id !== sId) {
                    return (
                      <Flex
                        key={sId}
                        alignItems="center"
                        cursor="pointer"
                        p="5px 10px"
                        borderRadius={isFromAts ? '6px' : '12px'}
                        _hover={{ background: '#F5F8FF' }}
                        _focus={{ background: '#F5F8FF' }}
                        onClick={() => {
                          onOpen();
                          setStatus(status);
                        }}
                      >
                        <KText
                          p="3px 10px"
                          borderRadius={'6px'}
                          color={color}
                          bgColor={bg}
                          size={'sm-medium'}
                          _groupHover={{
                            bg: 'white',
                            borderColor: 'border-color',
                          }}
                        >
                          {label}
                        </KText>
                      </Flex>
                    );
                  }
                  return null;
                })}
            </Flex>
          </MenuList>
        </Menu>
      );
    return (
      <Flex alignItems="center">
        <KText
          color={color}
          bgColor={bg}
          padding="5px 8px"
          borderRadius="6px"
          size={'sm-medium'}
          textAlign="center"
          _groupHover={{ bg: 'white', borderColor: 'border-color' }}
        >
          {label}
        </KText>
      </Flex>
    );
  };

  const getStatusText = (candidate, targetStatus) => {
    return (
      <KText mt="20px" size="sm-regular" color="secondary-700">
        Do you want to mark{' '}
        <Box as="span" fontWeight="medium" fontSize="sm" color="secondary-900">
          {candidate?.full_name || candidate?.first_name || candidate}
        </Box>{' '}
        <KText size="sm-regular" color="secondary-700" as="span">
          {' '}
          as{' '}
        </KText>
        <Box
          as="span"
          fontWeight="bold"
          fontSize="sm"
          color={targetStatus?.color}
        >
          {targetStatus?.label}
        </Box>
        ?
      </KText>
    );
  };

  const renderStatusMessageForm = () => {
    const requestReviewFormConfig = [
      {
        name: 'note',
        label: 'Feedback',
        type: 'textArea',
        isRequired: false,
        placeholder: 'Please share your feedback here',
      },
    ];

    return (
      <Box mt="10px">
        <FormBuilder
          config={requestReviewFormConfig}
          onSubmit={handleStatusChange}
          formRef={formRef}
        />
      </Box>
    );
  };

  const statusChangeModal = () => {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <KText size="md-medium" color="secondary-900">
              Review Candidate
            </KText>
            <SanitizedText size="sm" variant="muted" />
            {getStatusText(name, currentStatus)}
            {renderStatusMessageForm()}
          </ModalHeader>
          <ModalFooter>
            <Button
              variant="outline"
              mr={3}
              onClick={onClose}
              h="2rem"
              borderColor={'primary-500'}
            >
              <KText size="sm-medium" color="primary-500">
                Cancel
              </KText>
            </Button>
            <Button
              loadingText="Updating Status"
              isLoading={isLoading}
              onClick={() => {
                if (formRef.current) {
                  formRef.current.handleSubmit();
                }
              }}
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  if (status)
    return (
      <>
        {statusList()}
        {statusChangeModal()}
      </>
    );

  if (ats_enabled) {
    if (prospectId && isCreateAllowed) {
      return (
        <Flex pl="14px" alignItems="center" gap="10px">
          <IconButton
            w="34px"
            h="34px"
            aria-label="Add"
            cursor="pointer"
            icon={<Plus size={12} />}
            size="xs"
            borderRadius="full"
            background="secondary-50"
            color="black"
            _groupHover={{ bg: 'secondary-50', borderColor: 'border-color' }}
            onClick={modalActions?.onOpen}
          />
          <KText size="sm-regular" color="secondary-700">
            Ask for review
          </KText>
          {modalActions?.isOpen && (
            <ProjectOptions
              modalActions={modalActions}
              action={'review'}
              selected={[
                {
                  id: prospectId,
                  contact,
                },
              ]}
              toggleRowSelected={modalActions?.onClose}
            />
          )}
        </Flex>
      );
    }
  } else {
    if (prospectId && (isCreateAllowed || isUpdateAllowed)) {
      return (
        <Flex pl="14px" alignItems="center" gap="10px">
          <IconButton
            w="34px"
            h="34px"
            aria-label="Add"
            cursor="pointer"
            icon={<Plus size={12} />}
            size="xs"
            borderRadius="full"
            background="secondary-50"
            color="black"
            _groupHover={{ bg: 'secondary-50', borderColor: 'border-color' }}
            onClick={modalActions?.onOpen}
          />
          <KText size="sm-regular" color="secondary-700">
            Ask for review
          </KText>{' '}
          {modalActions?.isOpen && (
            <ProjectOptions
              modalActions={modalActions}
              action={'review'}
              selected={[
                {
                  id: prospectId,
                  contact,
                },
              ]}
              toggleRowSelected={modalActions?.onClose}
            />
          )}
        </Flex>
      );
    }
  }
  return isFromAts ? (
    <KText
      p="2px 8px"
      size="sm-medium"
      bg="#fff"
      w="fit-content"
      borderRadius="6px"
      color="secondary-700"
    >
      No review
    </KText>
  ) : (
    <KText pl="16px" color="secondary-700" size="sm-medium">
      No review
    </KText>
  );
};
